import React, { useRef, useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	CircularProgress,
	Alert,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import {
	getWebViewerContainerSx,
	getWebViewerInitOptions,
	prepareWebViewerInstance,
	setupWebViewerOnDocumentLoaded,
} from 'core/services/webViewerSetup';
import { encodeRemoteFileUrl } from 'core/utils/eventDocumentUrls';

export interface EventDocumentPdfEditorModalProps {
	open: boolean;
	onClose: () => void;
	/** Blob URL or remote PDF URL — parent manages blob lifecycle for local files */
	pdfUrl: string | null;
	documentTitle: string;
	onSave: (file: File) => void | Promise<void>;
}

/** Same toolbar profile as Onboarding document review (signature + image stamp). */
const WEBVIEWER_PROFILE = 'documentSigning' as const;

/**
 * Onscreen PDF editing using WebViewer (same stack / tools as onboarding document review).
 * Exports filled forms + annotations into a single PDF File on save.
 */
export function EventDocumentPdfEditorModal({
	open,
	onClose,
	pdfUrl,
	documentTitle,
	onSave,
}: EventDocumentPdfEditorModalProps): JSX.Element {
	const theme = useTheme();
	const isTabletViewport = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
	const viewer = useRef<HTMLDivElement>(null);
	const instance = useRef<any>(null);
	const [isViewerReady, setIsViewerReady] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [dialogReady, setDialogReady] = useState(false);
	const { showToast } = useToastContext();

	useEffect(() => {
		if (!open) {
			setDialogReady(false);
		}
	}, [open]);

	useEffect(() => {
		if (!open || !pdfUrl || !dialogReady) {
			return;
		}

		let cancelled = false;
		let objectUrl: string | null = null;

		const dispose = () => {
			if (instance.current) {
				try {
					instance.current.UI.dispose();
				} catch {
					/* ignore */
				}
				instance.current = null;
			}
			setIsViewerReady(false);
		};

		const loadSource = async (url: string): Promise<string> => {
			if (url.startsWith('blob:') || url.startsWith('data:')) {
				return url;
			}
			const encoded = encodeRemoteFileUrl(url);
			try {
				const response = await fetch(encoded, {
					mode: 'cors',
					credentials: 'omit',
					headers: { Accept: 'application/pdf' },
				});
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}
				const blob = await response.blob();
				if (!blob.size) {
					throw new Error('empty PDF');
				}
				objectUrl = URL.createObjectURL(blob);
				return objectUrl;
			} catch {
				return encoded;
			}
		};

		const run = async () => {
			dispose();
			setError(null);
			if (!viewer.current) {
				return;
			}

			const inst = await WebViewer(
				getWebViewerInitOptions(WEBVIEWER_PROFILE) as any,
				viewer.current
			);
			if (cancelled) {
				try {
					inst.UI.dispose();
				} catch {
					/* ignore */
				}
				return;
			}

			instance.current = inst;
			prepareWebViewerInstance(inst, WEBVIEWER_PROFILE);
			inst.Core.documentViewer.addEventListener('documentLoaded', () => {
				setupWebViewerOnDocumentLoaded(inst, WEBVIEWER_PROFILE);
			});
			inst.Core.documentViewer.addEventListener('documentLoadError', () => {
				setError('Failed to load PDF. Check the file or URL and try again.');
			});

			const source = await loadSource(pdfUrl);
			if (cancelled) {
				return;
			}
			inst.UI.loadDocument(source, { filename: 'document.pdf', extension: 'pdf' });
			setIsViewerReady(true);
		};

		run().catch((err) => {
			if (cancelled) return;
			console.error('WebViewer initialization error:', err);
			setError('Failed to initialize PDF viewer');
			setIsViewerReady(false);
		});

		return () => {
			cancelled = true;
			dispose();
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
		};
	}, [open, pdfUrl, dialogReady]);

	const handleSave = async () => {
		if (!instance.current) {
			showToast('error', 'PDF viewer not ready');
			return;
		}

		setIsSaving(true);
		setError(null);

		try {
			const { documentViewer, annotationManager } = instance.current.Core;
			const doc = documentViewer.getDocument();
			if (!doc) {
				showToast('error', 'No document loaded');
				return;
			}

			const xfdfString = await annotationManager.exportAnnotations();
			const data = await doc.getFileData({ xfdfString });
			const arr = new Uint8Array(data);
			const safeName =
				documentTitle
					.trim()
					.replace(/\.pdf$/i, '')
					.replace(/[^\w.-]+/g, '_')
					.replace(/^_+|_+$/g, '') || 'document';
			const file = new File([arr], `${safeName}.pdf`, { type: 'application/pdf' });

			await onSave(file);
			showToast('success', 'PDF saved');
			onClose();
		} catch (e) {
			console.error('Save PDF error:', e);
			setError('Could not export PDF. Please try again.');
			showToast('error', 'Failed to save PDF');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={isSaving ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			TransitionProps={{
				onEntered: () => setDialogReady(true),
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<DescriptionIcon />
					<Typography variant="h6">Edit PDF: {documentTitle}</Typography>
				</Box>
			</DialogTitle>

			<DialogContent>
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="body2">
						Fill form fields, add a signature, or insert an image. Click &quot;Save PDF&quot; to
						apply changes.
					</Typography>
				</Alert>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Box sx={getWebViewerContainerSx(isTabletViewport, 'modal')}>
					{(!isViewerReady || !pdfUrl) && open && (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								bgcolor: 'rgba(255, 255, 255, 0.9)',
								zIndex: 10,
							}}
						>
							<CircularProgress sx={{ color: 'rgb(68, 99, 136)' }} />
							<Typography variant="body2" sx={{ mt: 2 }}>
								Loading document viewer...
							</Typography>
						</Box>
					)}
					<div ref={viewer} style={{ height: '100%', width: '100%' }} />
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="outlined"
					onClick={onClose}
					disabled={isSaving}
					sx={{
						color: 'rgb(68, 99, 136)',
						borderColor: 'rgb(68, 99, 136)',
						'&:hover': {
							borderColor: 'rgb(58, 89, 126)',
							backgroundColor: 'rgba(68, 99, 136, 0.04)',
						},
					}}
				>
					Cancel
				</Button>
				<Box sx={{ flex: '1 1 auto' }} />
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={isSaving || !isViewerReady || !pdfUrl}
					startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : undefined}
					sx={{
						backgroundColor: 'rgb(68, 99, 136)',
						'&:hover': { backgroundColor: 'rgb(58, 89, 126)' },
					}}
				>
					{isSaving ? 'Saving...' : 'Save PDF'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
